import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

export const Reveal = ({ children, delay = 0, y = 28, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);

export const MaskedLines = ({ lines, className = "", lineClassName = "", delay = 0.15 }) => (
  <span className={className}>
    {lines.map((line, i) => (
      <span key={i} className="block overflow-hidden pb-[0.08em]">
        <motion.span
          className={`block ${lineClassName}`}
          initial={{ y: "110%" }}
          animate={{ y: 0 }}
          transition={{ duration: 0.9, delay: delay + i * 0.13, ease: [0.22, 1, 0.36, 1] }}
        >
          {line}
        </motion.span>
      </span>
    ))}
  </span>
);

export const SectionTag = ({ children, testId }) => (
  <p data-testid={testId} className="font-mono text-[11px] font-medium uppercase tracking-[0.3em] text-tark-cyan">
    {children}
  </p>
);

export const PrimaryButton = ({ to, children, testId, onClick, type }) => {
  const cls = "group inline-flex items-center gap-2 rounded-full bg-tark-blue px-7 py-3.5 font-display text-sm font-bold tracking-wide text-white transition-colors duration-300 hover:bg-tark-vivid focus:outline-none focus:ring-2 focus:ring-tark-cyan/60";
  const inner = (
    <>
      {children}
      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
    </>
  );
  if (to) return <Link to={to} data-testid={testId} className={cls} onClick={onClick}>{inner}</Link>;
  return <button type={type || "button"} data-testid={testId} className={cls} onClick={onClick}>{inner}</button>;
};

export const GhostButton = ({ to, children, testId }) => (
  <Link
    to={to}
    data-testid={testId}
    className="inline-flex items-center gap-2 rounded-full border border-tark-line px-7 py-3.5 font-display text-sm font-semibold tracking-wide text-tark-ink transition-colors duration-300 hover:border-tark-cyan/50 hover:text-tark-cyan"
  >
    {children}
  </Link>
);

export const Marquee = ({ items }) => (
  <div className="relative overflow-hidden border-y border-tark-line bg-tark-surface/60 py-5" data-testid="tech-marquee">
    <div className="flex w-max animate-marquee gap-0">
      {[0, 1].map((copy) => (
        <div key={copy} className="flex shrink-0 items-center" aria-hidden={copy === 1}>
          {items.map((item, i) => (
            <span key={i} className="flex items-center">
              <span className="px-8 font-display text-sm font-semibold uppercase tracking-[0.25em] text-tark-mut">{item}</span>
              <span className="h-1.5 w-1.5 rotate-45 bg-tark-cyan/70" />
            </span>
          ))}
        </div>
      ))}
    </div>
  </div>
);

export const TechTag = ({ children }) => (
  <span className="rounded-md border border-tark-line bg-tark-card px-3 py-1.5 font-mono text-xs text-tark-mut transition-colors duration-300 hover:border-tark-cyan/40 hover:text-tark-cyan">
    {children}
  </span>
);

export const PageHero = ({ eyebrow, lines, sub, children, image }) => (
  <section className="relative overflow-hidden pt-40 pb-20 sm:pb-28">
    {image && (
      <div className="absolute inset-0">
        <img src={image} alt="" className="h-full w-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-tark-bg/70 via-tark-bg/85 to-tark-bg" />
      </div>
    )}
    <div className="absolute inset-0 hero-grid opacity-40 [mask-image:radial-gradient(ellipse_70%_60%_at_50%_0%,black,transparent)]" />
    <div className="absolute inset-0 spotlight" />
    <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}>
        <SectionTag>{eyebrow}</SectionTag>
      </motion.div>
      <h1 className="mt-6 font-display text-4xl font-extrabold leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl">
        <MaskedLines lines={lines} />
      </h1>
      {sub && (
        <motion.p
          className="mt-7 max-w-2xl text-base leading-relaxed text-tark-mut sm:text-lg"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.55 }}
        >
          {sub}
        </motion.p>
      )}
      {children && (
        <motion.div
          className="mt-9 flex flex-wrap items-center gap-4"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.7 }}
        >
          {children}
        </motion.div>
      )}
    </div>
  </section>
);
